import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * Street View 4方向画像のオンデマンド取得 (docs/25 Increment 2、`fetchStreetViewImages` callable)。
 * ブラウザ用 Google Maps キーが無い現状の代替経路 — サーバの secret で都度取得し base64 で返す。
 * 画像は Storage/DB に一切保存しない (ToS §9-1)。同一 target+headings の組は
 * モジュールレベルの in-memory cache で再取得を避ける (メモリのみ、リロードで消える)。
 *
 * `target` は解析済み地点 (panoId 確定済み) と、新規地点解析フォームの事前プレビュー
 * (panoId 未確定、lat/lng のみ) の両方に対応する。
 */

export interface FetchedImage {
  data: string;
  mimeType: string;
  heading: number;
}

export type FetchImagesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; images: FetchedImage[]; panoId: string }
  | { status: 'error'; error: string };

export type FetchImagesTarget = { panoId: string } | { lat: number; lng: number };

interface FetchStreetViewImagesResult {
  ok: boolean;
  images?: FetchedImage[];
  panoId?: string;
  error?: string;
}

interface CachedResult {
  images: FetchedImage[];
  panoId: string;
}

const _cache = new Map<string, CachedResult>();

function targetCacheKey(target: FetchImagesTarget): string {
  return 'panoId' in target ? `pano:${target.panoId}` : `latlng:${target.lat},${target.lng}`;
}

export function useFetchStreetViewImages(
  districtId: string | null,
  target: FetchImagesTarget | null,
  headings: number[],
): FetchImagesState {
  const headingsKey = headings.join(',');
  const targetKey = target ? targetCacheKey(target) : null;
  const [state, setState] = useState<FetchImagesState>({ status: 'idle' });

  useEffect(() => {
    if (!districtId || !target || headings.length === 0) {
      setState({ status: 'idle' });
      return;
    }
    const cacheKey = `${targetKey}:${headingsKey}`;
    const cached = _cache.get(cacheKey);
    if (cached) {
      setState({ status: 'ready', images: cached.images, panoId: cached.panoId });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      const callable = httpsCallable<
        { districtId: string; panoId?: string; lat?: number; lng?: number; headings: number[] },
        FetchStreetViewImagesResult
      >(functions, 'fetchStreetViewImages');
      const res = await callable({ districtId, ...target, headings });
      if (cancelled) return;
      if (res.data.ok && res.data.images && res.data.panoId) {
        _cache.set(cacheKey, { images: res.data.images, panoId: res.data.panoId });
        setState({ status: 'ready', images: res.data.images, panoId: res.data.panoId });
      } else {
        setState({ status: 'error', error: res.data.error ?? '画像取得に失敗しました' });
      }
    })().catch((e: unknown) => {
      if (!cancelled) {
        setState({ status: 'error', error: e instanceof Error ? e.message : String(e) });
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtId, targetKey, headingsKey]);

  return state;
}
